import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, endpoints } from "../lib/api";
import { euro } from "../lib/format";
import { Lock, Clock, Sparkles, ArrowRight } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_salon-studio-pro-2/artifacts/7kyv63p4_LOGO%20LAKA%20CLUB%20BEAUTY.webp";

export default function PublicCatalog() {
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["public-services"],
    queryFn: () => api.get(endpoints.publicServices).then((r) => r.data),
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--laka-ivory)" }}>
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 border-b"
        style={{ backgroundColor: "#1A1A1A", borderColor: "#2a2a2a" }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 md:px-10 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded px-3 py-1.5" style={{ backgroundColor: "#FBF6F2" }}>
              <img src={LOGO} alt="LAKA Club Beauty" className="h-8 w-auto object-contain" data-testid="public-logo" />
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase hidden sm:block" style={{ color: "#D6B36A" }}>
              Estética avanzada &amp; Bienestar
            </div>
          </div>
          <Link
            to="/login"
            data-testid="public-login-link"
            className="text-xs tracking-widest uppercase px-4 py-2 rounded-md inline-flex items-center gap-2 transition-all"
            style={{ backgroundColor: "#D6B36A", color: "#1A1A1A" }}
          >
            <Lock size={12} strokeWidth={1.5} /> Acceso Studio
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 md:px-10 pt-20 pb-14 text-center max-w-4xl mx-auto">
        <div className="laka-eyebrow">Nuestros tratamientos</div>
        <h1 className="font-display text-5xl md:text-7xl mt-4 leading-[1.05]">
          Belleza avanzada,<br />
          <span style={{ color: "#D6B36A" }}>experiencias LAKA</span>
        </h1>
        <p className="mt-6 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "var(--laka-text)" }}>
          Descubre nuestros protocolos de estética, bienestar y tecnología. Cada tratamiento está diseñado
          para resultados visibles con la sofisticación LAKA Club Beauty.
        </p>
        <div className="mt-8 flex items-center justify-center gap-6 text-xs tracking-widest uppercase" style={{ color: "var(--laka-text)" }}>
          <span>Madrid · Salamanca</span>
          <span>·</span>
          <span>Reserva por WhatsApp</span>
        </div>
      </section>

      <div className="laka-divider max-w-3xl mx-auto my-4" />

      {/* Categories */}
      <main className="px-6 md:px-10 py-14 max-w-6xl mx-auto space-y-16" data-testid="public-catalog">
        {isLoading && (
          <div className="text-center py-24 text-sm" style={{ color: "var(--laka-text)" }}>Cargando catálogo…</div>
        )}
        {categories.map((cat, idx) => (
          <section key={cat.key} data-testid={`public-cat-${cat.key}`}>
            <div className="flex items-baseline gap-4 mb-2">
              <span className="font-display text-2xl" style={{ color: "#D6B36A" }}>
                {String(idx + 1).padStart(2, "0")}
              </span>
              <h2 className="font-display text-3xl md:text-4xl">{cat.label}</h2>
            </div>
            {cat.tagline && (
              <p className="text-sm italic max-w-2xl mb-8" style={{ color: "var(--laka-text)" }}>
                “{cat.tagline}”
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cat.services.map((s) => (
                <div
                  key={s.id}
                  className="laka-card p-6 flex items-start justify-between gap-4 hover:border-[#D6B36A] transition-colors"
                  data-testid={`public-service-${s.id}`}
                >
                  <div className="flex-1">
                    <h3 className="font-medium text-base leading-snug">{s.name}</h3>
                    {s.description && (
                      <p className="text-xs mt-2" style={{ color: "var(--laka-text)" }}>{s.description}</p>
                    )}
                    <div className="mt-3 flex items-center gap-2 text-[11px]" style={{ color: "var(--laka-text)" }}>
                      <Clock size={12} strokeWidth={1.25} />
                      {formatDuration(s.duration_min)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-3xl leading-none" style={{ color: "#D6B36A" }}>
                      {euro(s.price)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </main>

      {/* CTA */}
      <section className="px-6 md:px-10 py-20" style={{ backgroundColor: "#1A1A1A" }}>
        <div className="max-w-3xl mx-auto text-center" style={{ color: "#FBF6F2" }}>
          <Sparkles size={22} strokeWidth={1} className="mx-auto" style={{ color: "#D6B36A" }} />
          <h2 className="font-display text-4xl md:text-5xl mt-4">Reserva tu experiencia</h2>
          <p className="text-sm mt-4" style={{ color: "#B8B0A6" }}>
            Contacta con nosotras para elegir el tratamiento perfecto según tu piel y objetivos.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://wa.me/34910000000"
              target="_blank"
              rel="noreferrer"
              data-testid="public-whatsapp"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium transition-all"
              style={{ backgroundColor: "#D6B36A", color: "#1A1A1A" }}
            >
              Escríbenos por WhatsApp <ArrowRight size={14} strokeWidth={1.5} />
            </a>
            <Link
              to="/login"
              data-testid="public-cta-login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm border transition-all hover:bg-[#242424]"
              style={{ borderColor: "#3a3a3a", color: "#FBF6F2" }}
            >
              <Lock size={12} strokeWidth={1.5} /> Acceso Studio
            </Link>
          </div>
        </div>
      </section>

      <footer className="text-center py-8 text-[10px] tracking-widest uppercase" style={{ color: "var(--laka-text)" }}>
        © LAKA Club Beauty · Calle Serrano, 88 · Madrid
      </footer>
    </div>
  );
}

function formatDuration(min) {
  if (!min) return "—";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (m === 0) return h === 1 ? "1 h" : `${h} h`;
  return `${h} h ${m} min`;
}
